import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import type { TeachStyle } from '@/mocks/lessons';

interface TeachDifferentlyProps {
  styles: TeachStyle[];
}

export default function TeachDifferently({ styles }: TeachDifferentlyProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [activeIndex, setActiveIndex] = useState(0);
  const active = styles[activeIndex];

  const t = {
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textMuted: isDark ? 'text-white/30' : 'text-black/30',
    textFaint: isDark ? 'text-white/25' : 'text-black/25',
    iconBg: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]',
    iconMuted: isDark ? 'text-white/50' : 'text-black/40',
    pillActive: isDark ? 'bg-white text-black' : 'bg-black text-white',
    pillInactive: isDark ? 'bg-white/[0.04] border-white/[0.06] text-white/50 hover:bg-white/[0.08]' : 'bg-black/[0.04] border-black/[0.06] text-black/50 hover:bg-black/[0.08]',
    numBg: isDark ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-black/[0.04] border-black/[0.06]',
    numText: isDark ? 'text-white/25' : 'text-black/25',
    contentText: isDark ? 'text-white/60' : 'text-black/60',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
      className="px-5 pb-4"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className={`w-8 h-8 rounded-lg ${t.iconBg} flex items-center justify-center`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-refresh-line ${t.iconMuted} text-sm`} />
          </div>
        </div>
        <div>
          <h3 className={`${t.textPrimary} text-sm font-semibold`}>Teach Differently</h3>
          <p className={`${t.textFaint} text-[11px]`}>{styles.length} alternate explanations</p>
        </div>
      </div>

      {/* Style selector pills */}
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar pb-1">
        {styles.map((s, i) => (
          <motion.button
            key={s.style}
            onClick={() => setActiveIndex(i)}
            whileTap={{ scale: 0.92 }}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 cursor-pointer transition-all duration-200 border ${
              i === activeIndex ? t.pillActive : t.pillInactive
            }`}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className={`${s.icon} text-[10px]`} />
            </div>
            {s.title}
          </motion.button>
        ))}
      </div>

      {/* Active style content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active.style}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          className="space-y-3"
        >
          {active.content.map((line, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06, duration: 0.3 }}
              className="flex items-start gap-3"
            >
              <div className={`w-5 h-5 rounded-md ${t.numBg} border flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <span className={`${t.numText} text-[10px] font-bold`}>{i + 1}</span>
              </div>
              <p className={`${t.contentText} text-xs leading-relaxed`}>{line}</p>
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}